import { AppStore } from '../state/store';
import { DEFAULT_TRACK_SHAPE, TrackShapeSettings, TrackType } from '../core/geometry/gearRack';
import { bindPanelCollapse, panelEnd, panelStart } from './collapsiblePanel';
import { sidebarPane } from './sidebarTabs';
import { drawTrackShapeViewer } from './trackShapeViewer';

type NumParamKey = {
  [K in keyof TrackShapeSettings]: TrackShapeSettings[K] extends number ? K : never;
}[keyof TrackShapeSettings];

const RAIL_SLIDERS: { key: NumParamKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'railDiameter', label: 'Track beam diameter (m)', min: 0.04, max: 0.4, step: 0.01 },
  { key: 'railSpacing', label: 'Track beam spacing (m)', min: 0.4, max: 2.5, step: 0.05 },
  { key: 'flatBeamHeight', label: 'Flat beam height (m)', min: 0.02, max: 0.3, step: 0.01 },
  { key: 'triangleWidth', label: 'Triangle width (m)', min: 0.2, max: 2.5, step: 0.05 },
  { key: 'triangleHeight', label: 'Triangle height (m)', min: 0.15, max: 2.0, step: 0.05 },
];

const TYPE2_SLIDERS: { key: NumParamKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'type2TopGap', label: 'Space between top parts (m)', min: 0.2, max: 2.5, step: 0.05 },
  { key: 'type2BottomWidth', label: 'Bottom width (m)', min: 0, max: 2.5, step: 0.05 },
  { key: 'type2VHeight', label: 'Height of the V (m)', min: 0, max: 2.0, step: 0.05 },
  { key: 'type2RackTopHeight', label: 'Rack Top Section (m)', min: 0, max: 0.6, step: 0.01 },
  { key: 'type2RackTopWidth', label: 'Width at rack top (m)', min: 0, max: 2.5, step: 0.05 },
  { key: 'type2RackBottomHeight', label: 'Rack Bottom Section (m)', min: 0, max: 0.6, step: 0.01 },
  { key: 'type2BeamHeight', label: 'Beam Part (m)', min: 0, max: 0.6, step: 0.01 },
  { key: 'type2CircleDiameter', label: 'Circle diameter (m)', min: 0.04, max: 0.4, step: 0.01 },
  { key: 'type2Thickness', label: 'Thickness of the V lines (m)', min: 0.01, max: 0.15, step: 0.005 },
];

/** Shown in place of the hint once the V sides are switched to plates. */
const PLATE_SLIDERS: { key: NumParamKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'type2PlateThickness', label: 'Thickness of the plates (m)', min: 0.01, max: 0.5, step: 0.005 },
  { key: 'type2PlateGap', label: 'Distance between plates (m)', min: 0.02, max: 1.5, step: 0.01 },
];

const RACK_SLIDERS: { key: NumParamKey; label: string; min: number; max: number; step: number }[] = [
  { key: 'pitch', label: 'Tooth pitch (m)', min: 0.04, max: 0.4, step: 0.01 },
  { key: 'toothHeight', label: 'Tooth height (m)', min: 0.01, max: 0.2, step: 0.005 },
  { key: 'toothWidth', label: 'Tooth width (m)', min: 0.05, max: 0.5, step: 0.01 },
  { key: 'toothThickness', label: 'Tooth base (m)', min: 0.01, max: 0.2, step: 0.005 },
];

// Type 2 takes its tooth height from the rack top + bottom sections instead.
const TYPE2_RACK_SLIDERS = RACK_SLIDERS.filter((p) => p.key !== 'toothHeight');

/** Track cross-section and gear-rack settings (Print tab). */
export class TrackShapePanel {
  private el: HTMLElement;

  constructor(private store: AppStore) {
    this.el = document.createElement('div');
    sidebarPane('print').appendChild(this.el);
    store.on('doc', () => this.render());
    store.on('track', () => this.render());
    this.render();
  }

  private render(): void {
    const shape = this.store.doc.trackShape;
    const type2 = shape.trackType === 'type2';

    this.el.innerHTML = `
      ${panelStart('trackShape', 'Track Shape')}
        <div class="row">
          <label>Track type</label>
          <select data-action="trackType">
            <option value="type1" ${shape.trackType === 'type1' ? 'selected' : ''}>Type 1</option>
            <option value="type2" ${type2 ? 'selected' : ''}>Type 2</option>
          </select>
        </div>
        <div class="track-shape-viewer-wrap">
          <svg class="track-shape-viewer" aria-label="Track cross-section"></svg>
        </div>
        ${
          type2
            ? `<p class="hint">V-spine with rail circles, rack squares, and beam. Part names in amber, measurements in millimetres.</p>
        ${TYPE2_SLIDERS.map((p) => slider(p.key, p.label, shape[p.key], p.min, p.max, p.step)).join('')}
        <div class="row"><label><input type="checkbox" data-action="solid-webs" ${
          shape.type2SolidWebs ? 'checked' : ''
        }> Solid V sides</label></div>
        ${
          shape.type2SolidWebs
            ? '<p class="hint">Off cuts the sides between the rail circles and the rack top into plates spaced along the track.</p>'
            : PLATE_SLIDERS.map((p) => slider(p.key, p.label, shape[p.key], p.min, p.max, p.step)).join('')
        }`
            : `<p class="hint">Black track beams, yellow flat beam, blue triangle. Part names in amber, measurements in millimetres.</p>
        ${RAIL_SLIDERS.map((p) => slider(p.key, p.label, shape[p.key], p.min, p.max, p.step)).join('')}`
        }
        <div class="row">
          <button type="button" data-action="reset-shape">Reset defaults</button>
        </div>
      ${panelEnd()}
      ${panelStart('gearRack', 'Gear Rack')}
        <p class="hint">${
          type2
            ? `Fills the rack top &amp; bottom sections — tooth height is those two added together (${fmt(
                shape.type2RackTopHeight + shape.type2RackBottomHeight,
              )} m).`
            : 'Sits on the flat beam.'
        }</p>
        <div class="row"><label><input type="checkbox" data-action="enabled" ${shape.enabled ? 'checked' : ''}> Enabled</label></div>
        ${(type2 ? TYPE2_RACK_SLIDERS : RACK_SLIDERS)
          .map((p) => slider(p.key, p.label, shape[p.key], p.min, p.max, p.step))
          .join('')}
        <div class="row">
          <button type="button" data-action="reset-rack">Reset defaults</button>
        </div>
      ${panelEnd()}`;

    bindPanelCollapse(this.el);
    const viewer = this.el.querySelector<SVGSVGElement>('.track-shape-viewer');
    if (viewer) drawTrackShapeViewer(viewer, shape);

    this.el.querySelectorAll<HTMLInputElement>('input[type="range"]').forEach((input) => {
      const valEl = input.parentElement!.querySelector('.val')!;
      input.addEventListener('input', () => {
        valEl.textContent = fmt(parseFloat(input.value));
        this.previewViewer();
      });
      input.addEventListener('change', () => {
        const key = input.dataset.key as NumParamKey;
        const value = parseFloat(input.value);
        this.store.commit((d) => {
          d.trackShape[key] = value;
          if (key === 'railSpacing' && d.trackShape.triangleWidth > value) {
            d.trackShape.triangleWidth = value;
          }
          if (key === 'triangleWidth' && value > d.trackShape.railSpacing) {
            d.trackShape.triangleWidth = d.trackShape.railSpacing;
          }
        });
      });
    });

    this.el.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      input.addEventListener('change', () => {
        if (input.dataset.action === 'enabled') {
          this.store.commit((d) => {
            d.trackShape.enabled = input.checked;
          });
        } else if (input.dataset.action === 'solid-webs') {
          this.store.commit((d) => {
            d.trackShape.type2SolidWebs = input.checked;
          });
        }
      });
    });

    this.el.querySelector<HTMLSelectElement>('select[data-action="trackType"]')?.addEventListener(
      'change',
      (ev) => {
        const value = (ev.target as HTMLSelectElement).value;
        const trackType: TrackType = value === 'type2' ? 'type2' : 'type1';
        this.store.commit((d) => {
          d.trackShape.trackType = trackType;
        });
      },
    );

    this.el.querySelectorAll<HTMLButtonElement>('button[data-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.dataset.action === 'reset-shape') {
          this.store.commit((d) => {
            if (d.trackShape.trackType === 'type2') {
              for (const p of TYPE2_SLIDERS) d.trackShape[p.key] = DEFAULT_TRACK_SHAPE[p.key];
              for (const p of PLATE_SLIDERS) d.trackShape[p.key] = DEFAULT_TRACK_SHAPE[p.key];
              d.trackShape.type2SolidWebs = DEFAULT_TRACK_SHAPE.type2SolidWebs;
            } else {
              for (const p of RAIL_SLIDERS) d.trackShape[p.key] = DEFAULT_TRACK_SHAPE[p.key];
            }
          });
        } else if (btn.dataset.action === 'reset-rack') {
          this.store.commit((d) => {
            d.trackShape.enabled = DEFAULT_TRACK_SHAPE.enabled;
            for (const p of RACK_SLIDERS) d.trackShape[p.key] = DEFAULT_TRACK_SHAPE[p.key];
          });
        }
      });
    });
  }

  private previewViewer(): void {
    const viewer = this.el.querySelector<SVGSVGElement>('.track-shape-viewer');
    if (!viewer) return;
    const draft: TrackShapeSettings = { ...this.store.doc.trackShape };
    this.el.querySelectorAll<HTMLInputElement>('input[type="range"][data-key]').forEach((input) => {
      const key = input.dataset.key as NumParamKey;
      draft[key] = parseFloat(input.value);
    });
    drawTrackShapeViewer(viewer, draft);
  }
}

/** @deprecated Use {@link TrackShapePanel}. */
export const GearRackPanel = TrackShapePanel;

function slider(key: string, label: string, value: number, min: number, max: number, step: number): string {
  return `<div class="row"><label>${label}</label>
    <input type="range" data-key="${key}" min="${min}" max="${max}" step="${step}" value="${value}">
    <span class="val">${fmt(value)}</span></div>`;
}

function fmt(v: number): string {
  return v >= 10 ? v.toFixed(0) : v >= 1 ? v.toFixed(1) : v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}
